import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { getEmpresa, getEmpresas, getEjercicios, getProyeccionConfig, saveProyeccionConfig } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "lucide-react";
import {
  TrendingUp,
  Package,
  Users,
  Building2,
  Landmark,
  Layers,
  BarChart3,
  LayoutDashboard,
  Construction,
  Wallet,
  ArrowLeftRight,
  ClipboardPaste,
  Map,
  Settings2,
} from "lucide-react";

// Import driver components
import DriverPygIngresosContent from "@/components/proyeccion/DriverPygIngresosContent";
import DriverPygOtrosIngresosContent from "@/components/proyeccion/DriverPygOtrosIngresosContent";
import DriverPygGastosContent from "@/components/proyeccion/DriverPygGastosContent";
import ResultadosProyeccionContent from "@/components/proyeccion/ResultadosProyeccionContent";
import EscenariosContent from "@/components/proyeccion/EscenariosContent";
import CapexContent from "@/components/proyeccion/CapexContent";
import FinanciacionContent from "@/components/proyeccion/FinanciacionContent";
import NofContent from "@/components/proyeccion/NofContent";
import OtrasHipotesisContent from "@/components/proyeccion/OtrasHipotesisContent";
import PlanContent from "@/components/proyeccion/PlanContent";
import MapaHipotesisContent from "@/components/proyeccion/MapaHipotesisContent";
import GeneralesContent from "@/components/proyeccion/GeneralesContent";
import ProyeccionDefaultButton from "@/components/proyeccion/ProyeccionDefaultButton";

// Placeholder for tabs not yet implemented
function ProximamenteTab({ titulo, descripcion, icono: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="p-4 rounded-full bg-muted mb-4">
        <Construction className="h-12 w-12 text-muted-foreground" />
      </div>
      <h3 className="text-xl font-semibold mb-2">{titulo}</h3>
      <p className="text-muted-foreground max-w-md">{descripcion}</p>
      <Badge variant="outline" className="mt-4">Próximamente</Badge>
    </div>
  );
}

// Tab configuration
// Petición de Pau (nov-2026): toda hipótesis del modelo se toca dentro de
// Drivers y en un solo sitio. «Generales» (impuesto, IPC, payout, nivel)
// es la primera pestaña y la que se abre por defecto.
const tabsConfig = [
  {
    id: "generales",
    label: "Generales",
    icon: Settings2,
    implementado: true,
    descripcion: "Hipótesis globales: impuesto, IPC, payout y nivel de proyección"
  },
  { 
    id: "pyg-ingresos", 
    label: "PyG – Ingresos", 
    icon: TrendingUp,
    implementado: true,
    descripcion: "Proyección de líneas de ingresos (Importe neto cifra de negocios)"
  },
  { 
    id: "pyg-otros", 
    label: "PyG – Otros ingresos", 
    icon: Package,
    implementado: true,
    descripcion: "Proyección de otros ingresos de explotación"
  },
  {
    id: "pyg-gastos",
    label: "PyG – Gastos",
    icon: Users,
    implementado: true,
    descripcion: "Proyección de gastos (aprovisionamientos, personal, otros)"
  },
  {
    id: "capex",
    label: "CAPEX",
    icon: Building2,
    implementado: true,
    descripcion: "Proyección de inversiones en activos fijos"
  },
  {
    id: "financiacion",
    label: "Financiación",
    icon: Landmark,
    implementado: true,
    descripcion: "Proyección de deuda y estructura financiera"
  },
  {
    id: "otras-hipotesis",
    label: "Otras hipótesis",
    icon: ArrowLeftRight,
    implementado: true,
    descripcion: "Movimientos manuales de balance en años futuros (partida doble)"
  },
  {
    id: "plan",
    label: "Plan",
    icon: ClipboardPaste,
    implementado: true,
    descripcion: "Pegar un plan de hipótesis completo (previsualizar, aplicar por partes, deshacer)"
  },
  {
    id: "mapa",
    label: "Mapa de hipótesis",
    icon: Map,
    implementado: true,
    descripcion: "Todos los drivers, operaciones y parámetros de la proyección en un solo sitio, con fecha"
  },
  {
    id: "escenarios",
    label: "Escenarios",
    icon: Layers,
    implementado: true,
    descripcion: "Gestión de escenarios (base, optimista, pesimista)"
  },
  // "Comparativa" y "Coste de personal" (placeholders "Próximamente")
  // eliminados a petición de Héctor (oct-2026, puntos 8 y 10): las
  // comparativas reales viven en el menú lateral con histórico+proyectado.
  {
    id: "resultados",
    label: "Resultados",
    icon: BarChart3,
    implementado: true,
    descripcion: "Resultados consolidados de la proyección"
  },
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    implementado: false,
    descripcion: "Dashboard visual de la proyección financiera"
  },
  {
    id: "nof",
    label: "NOF",
    icon: Wallet,
    implementado: true,
    descripcion: "Necesidades Operativas de Fondos proyectadas"
  },
];

export default function ProyeccionPage() {
  const { empresaId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [empresa, setEmpresa] = useState(null);
  const [ejercicios, setEjercicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [horizonteAnios, setHorizonteAnios] = useState(1);
  // A1 (Hector 29-09): ANIO BASE de la proyeccion. null = ultimo real (de
  // siempre). Con anio base, el modelo ignora los ejercicios posteriores y
  // las pestanas reciben los ejercicios recortados para que sus cabeceras
  // de anios coincidan con el backend.
  const [anioBase, setAnioBase] = useState(null);
  const [myRole, setMyRole] = useState("owner");
  const [refreshKey, setRefreshKey] = useState(0);
  // Nivel de detalle POR PESTAÑA (queja Pau 30-09, revierte el selector global
  // del 07-09): cada tabla de drivers lleva su propio selector 1D/3D/MAX,
  // recordado por empresa y pestaña (prop `storageKey` de TablaDrivers).

  const activeTab = searchParams.get("tab") || "generales";
  const readOnly = myRole === "read";

  useEffect(() => {
    loadData();
  }, [empresaId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [empresaRes, ejerciciosRes, configRes, todasRes] = await Promise.all([
        getEmpresa(empresaId),
        getEjercicios(empresaId),
        getProyeccionConfig(empresaId),
        getEmpresas()
      ]);
      setEmpresa(empresaRes.data);
      // Determinar my_role mirando la lista unificada (incluye shared).
      const found = (todasRes.data || []).find((e) => e.id === empresaId);
      setMyRole(found?.my_role || "owner");
      const ejerciciosConDatos = ejerciciosRes.data
        .filter(e => e.total_cuentas > 0)
        .sort((a, b) => b.anio - a.anio);
      setEjercicios(ejerciciosConDatos);
      setHorizonteAnios(configRes.data?.horizonte_anios || 1);
      setAnioBase(configRes.data?.anio_base ?? null);
    } catch (error) {
      toast.error("Error al cargar datos de la empresa");
    } finally {
      setLoading(false);
    }
  };

  const handleHorizonteChange = useCallback(async (value) => {
    const newHorizonte = parseInt(value);
    const anterior = horizonteAnios;
    setHorizonteAnios(newHorizonte);
    try {
      await saveProyeccionConfig(empresaId, { horizonte_anios: newHorizonte });
      // Las pestañas cargan el consolidado con [empresaId]: sin remontar,
      // Resultados/NOF/CAPEX/Financiación seguían con los años viejos.
      setRefreshKey((k) => k + 1);
    } catch (error) {
      setHorizonteAnios(anterior);
      toast.error("Error al guardar el horizonte");
    }
  }, [empresaId, horizonteAnios]);

  const handleAnioBaseChange = useCallback(async (value) => {
    const nuevo = value === "auto" ? null : parseInt(value);
    setAnioBase(nuevo);
    try {
      await saveProyeccionConfig(empresaId, { anio_base: nuevo });
      setRefreshKey((k) => k + 1);
      toast.success(
        nuevo
          ? `Proyeccion anclada en ${nuevo}: los anios reales posteriores se ignoran en el modelo`
          : "Proyeccion desde el ultimo anio real"
      );
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al guardar el anio base");
    }
  }, [empresaId]);

  // Ejercicios que ven las pestanas: recortados al anio base si lo hay.
  const ejerciciosVisibles = anioBase
    ? ejercicios.filter((e) => e.anio <= anioBase)
    : ejercicios;

  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!empresa) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Empresa no encontrada</p>
      </div>
    );
  }

  const renderTabContent = (tabId) => {
    switch (tabId) {
      case "generales":
        return <GeneralesContent empresaId={empresaId} readOnly={readOnly} />;
      case "pyg-ingresos":
        return <DriverPygIngresosContent empresaId={empresaId} ejercicios={ejerciciosVisibles} aniosProyectar={horizonteAnios} readOnly={readOnly} />;
      case "pyg-otros":
        return <DriverPygOtrosIngresosContent empresaId={empresaId} ejercicios={ejerciciosVisibles} aniosProyectar={horizonteAnios} readOnly={readOnly} />;
      case "pyg-gastos":
        return <DriverPygGastosContent empresaId={empresaId} ejercicios={ejerciciosVisibles} aniosProyectar={horizonteAnios} readOnly={readOnly} />;
      case "capex":
        return <CapexContent empresaId={empresaId} ejercicios={ejerciciosVisibles} aniosProyectar={horizonteAnios} readOnly={readOnly} />;
      case "financiacion":
        return <FinanciacionContent empresaId={empresaId} ejercicios={ejerciciosVisibles} aniosProyectar={horizonteAnios} readOnly={readOnly} />;
      case "otras-hipotesis":
        return <OtrasHipotesisContent empresaId={empresaId} ejercicios={ejerciciosVisibles} aniosProyectar={horizonteAnios} readOnly={readOnly} />;
      case "plan":
        return <PlanContent empresaId={empresaId} readOnly={readOnly} />;
      case "mapa":
        return <MapaHipotesisContent empresaId={empresaId} onIr={(tab) => setSearchParams({ tab })} />;
      case "escenarios":
        return <EscenariosContent empresaId={empresaId} readOnly={readOnly} />;
      case "resultados":
        return <ResultadosProyeccionContent empresaId={empresaId} readOnly={readOnly} soloPanel />;
      case "nof":
        return <NofContent empresaId={empresaId} readOnly={readOnly} />;
      default:
        const tabConfig = tabsConfig.find(t => t.id === tabId);
        return (
          <ProximamenteTab 
            titulo={tabConfig?.label || "Módulo"} 
            descripcion={tabConfig?.descripcion || "Este módulo estará disponible próximamente"}
            icono={tabConfig?.icon || Construction}
          />
        );
    }
  };

  // Último año disponible para preview
  const ultimoAnio = ejercicios.length > 0 ? ejercicios[0].anio : new Date().getFullYear();
  // A1: si hay anio base, la proyeccion arranca desde el (no desde el ultimo real).
  const anioArranque = anioBase ?? ultimoAnio;

  return (
    <div className="space-y-6" data-testid="proyeccion-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Proyección Financiera</h1>
          <p className="text-muted-foreground">{empresa.nombre} • Drivers de proyección</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ProyeccionDefaultButton onSaved={() => setRefreshKey((k) => k + 1)} />
          {/* El nivel de detalle se elige EN CADA PESTAÑA/TABLA (queja Pau
              30-09); ya no hay selector global aquí. */}
          {/* A1 (Hector 29-09): anio base de la proyeccion. Con base != ultimo
              real, se marca en ambar: los ejercicios posteriores se ignoran. */}
          <div className={anioBase
            ? "flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30"
            : "flex items-center gap-2 px-4 py-2 rounded-lg border bg-muted/30"}
            data-testid="anio-base-selector">
            <Label className="text-sm font-medium whitespace-nowrap">Año base:</Label>
            <Select value={anioBase ? String(anioBase) : "auto"} onValueChange={handleAnioBaseChange} disabled={readOnly}>
              <SelectTrigger className="w-[190px] h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Último real ({ultimoAnio})</SelectItem>
                {ejercicios.filter((e) => e.anio < ultimoAnio).map((e) => (
                  <SelectItem key={e.anio} value={String(e.anio)}>
                    {e.anio} — ignora {e.anio + 1}+
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg border bg-muted/30" data-testid="horizonte-global-selector">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium whitespace-nowrap">Horizonte:</Label>
            <Select value={horizonteAnios.toString()} onValueChange={handleHorizonteChange} disabled={readOnly}>
              <SelectTrigger className="w-[160px] h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* E11 (Pau oct-2026): hasta 10 años — un préstamo bullet a
                    5+ años necesita ver su devolución/refinanciación. El
                    backend ya validaba 1-10. */}
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <SelectItem key={n} value={n.toString()}>
                    {n} año{n > 1 ? "s" : ""} ({anioArranque + 1}–{anioArranque + n})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Badge variant="outline" className="text-xs">
            {ejercicios.length} ejercicios
          </Badge>
        </div>
      </div>

      {/* Tabs de Drivers */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div>
          <TabsList className="h-auto flex flex-wrap justify-start gap-1 rounded-2xl">
            {tabsConfig.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="relative px-4"
                  data-testid={`tab-${tab.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="text-[13px] font-medium">{tab.label}</span>
                    {!tab.implementado && (
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
                    )}
                  </div>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {/* Tab Contents */}
        {tabsConfig.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} className="mt-6">
            <div key={refreshKey}>{renderTabContent(tab.id)}</div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
